import type { RawDraftContentState } from 'draft-js';
import { useMemo } from 'react';
import { isString } from 'lodash';

import { ElementEditor } from '../../../elements/elementEditorConstants';
import { convertElementDraftContentToTiptap, DocumentType } from './elementConversion';
import convertSimpleStringToTiptapContent from '../../utils/createJsonContentUtils/convertSimpleStringToTiptapContent';
import { getEditorType } from '../../utils/editorTypeUtils';
import { asObject, ImMap } from '../../../utils/immutableHelper';
import { TiptapContent, TiptapNodeType } from '../../tiptapTypes';
import { manuallyReportError } from '../../../../client/analytics/rollbarService';

const EMPTY_TIPTAP_CONTENT: TiptapContent = {
    type: 'doc',
    content: [{ type: TiptapNodeType.paragraph }],
};

/**
 * Gets the Tiptap content of an element, converting it from DraftJS if necessary.
 * If the conversion fails or was postponed recently, returns an empty Tiptap content.
 * @returns: tiptap content
 *
 * This will likely need to be kept forever, unless we migrate all elements to Tiptap.
 */
export const useElementTiptapContent = (
    textContent: ImMap<TiptapContent> | ImMap<RawDraftContentState> | string | null,
    elementId?: string,
    // This will cause a different conversion process to be used for different editors.
    //  E.g. Captions will have their paragraphs converted to soft-new-lines first
    documentType?: DocumentType,
): TiptapContent => {
    const isTiptap = getEditorType(textContent) === ElementEditor.Tiptap;

    return useMemo(() => {
        // It's a tiptap card, just return its content
        if (isTiptap) return asObject(textContent as ImMap<TiptapContent>);

        // Empty card; return some empty content
        // NOTE: This assumes that all element editors allow paragraphs. Currently, this is correct,
        //  but if this changes we'll need to update this logic.
        if (!textContent) return EMPTY_TIPTAP_CONTENT;

        if (isString(textContent)) return convertSimpleStringToTiptapContent(textContent);

        const data = asObject(textContent as ImMap<RawDraftContentState>) as RawDraftContentState;
        try {
            // Convert draft content to tiptap content without writing it back to the element
            return convertElementDraftContentToTiptap(data, documentType);
        } catch (e) {
            console.error('Error converting to tiptap', data);
            console.error(e);
            // TODO: Once we have a logger interface in the common area (that chooses client or server logger), use that instead.
            // This is using a deprecated function, do not copy this pattern elsewhere.
            manuallyReportError({
                errorMessage: 'Error converting to tiptap',
                error: e,
                custom: {
                    elementId,
                },
            });
            return EMPTY_TIPTAP_CONTENT;
        }
    }, [isTiptap, textContent, documentType, elementId]);
};
